import {
  LINES_OF_WORK,
  formatE164,
  partnerDisplayName,
  partnerLineOfWork,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchPartners,
  signCardImages,
  type Partner,
} from "../../lib/partnerQueries";

/**
 * The business card file: every card anybody has scanned, and what line of
 * work the person on it is in.
 *
 * This is the whole Partners section while the partner network is switched
 * off (ADR 0262). Jonathon, 2026-10-04: "basically a place where we can see
 * all the business cards and what line of work they're in." So it is the
 * cards themselves, grouped by line of work, and nothing about texts,
 * consent or referrals, which belong to the network.
 *
 * Grouped by the specific line of work ("Plumber", "Electrician"), not the
 * broad partner category: "i want the classification to be more specific like
 * electrician or plumber instead of trade etc" (ADR 0262).
 */

/** The listed lines of work first, in the list's order; the rest A to Z. */
const LISTED_ORDER = new Map(
  LINES_OF_WORK.map((entry, index) => [entry.label, index]),
);
function byLineOfWork(a: string, b: string): number {
  if (a === "Other" || b === "Other") return a === "Other" ? 1 : -1;
  const ai = LISTED_ORDER.get(a) ?? Infinity;
  const bi = LISTED_ORDER.get(b) ?? Infinity;
  return ai !== bi ? ai - bi : a.localeCompare(b);
}
export function CardFile({ search }: { search: string }) {
  const partnersQuery = useAsync(fetchPartners, []);
  const [lineOfWork, setLineOfWork] = useState("");

  const partners = useMemo(
    () => partnersQuery.data ?? [],
    [partnersQuery.data],
  );

  // One request for every front, rather than one per tile.
  const frontPaths = partners
    .map((partner) => partner.card_front_path)
    .filter((path): path is string => Boolean(path));
  const imagesQuery = useAsync(
    () => signCardImages(frontPaths),
    [frontPaths.join("|")],
  );
  const images = imagesQuery.data ?? {};

  const matching = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return partners;
    return partners.filter((partner) =>
      [
        partner.first_name,
        partner.last_name,
        partner.company,
        partner.title,
        partner.met_at,
        partner.city,
        partner.email,
        partnerLineOfWork(partner),
        formatE164(partner.phone_mobile),
        formatE164(partner.phone_office),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query)),
    );
  }, [partners, search]);

  /** Lines of work that have at least one card. */
  const counts = useMemo(() => {
    const byLine = new Map<string, number>();
    for (const partner of matching) {
      const line = partnerLineOfWork(partner);
      byLine.set(line, (byLine.get(line) ?? 0) + 1);
    }
    return [...byLine.keys()]
      .sort(byLineOfWork)
      .map((value) => ({ value, count: byLine.get(value) ?? 0 }));
  }, [matching]);

  const groups = useMemo(
    () =>
      counts
        .filter(({ value }) => !lineOfWork || value === lineOfWork)
        .map(({ value }) => ({
          value,
          cards: matching
            .filter((partner) => partnerLineOfWork(partner) === value)
            .sort((a, b) =>
              partnerDisplayName(a).localeCompare(partnerDisplayName(b)),
            ),
        })),
    [counts, matching, lineOfWork],
  );

  if (partnersQuery.error) {
    return (
      <div className="list">
        <div className="empty">
          <b>Could not load the business cards</b>
          {partnersQuery.error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="listhead">
        <h2>Business cards</h2>
        <div className="lh-right">
          <span className="count">
            {matching.length} {matching.length === 1 ? "card" : "cards"}
          </span>
          <button
            className="btn-primary"
            onClick={() => navigate("partner=new")}
          >
            + Scan a card
          </button>
        </div>
      </div>

      {counts.length > 0 && (
        <div className="partner-tabs" aria-label="Line of work">
          <button
            className={`card-tab${lineOfWork === "" ? " active" : ""}`}
            onClick={() => setLineOfWork("")}
          >
            Every line of work ({matching.length})
          </button>
          {counts.map(({ value, count }) => (
            <button
              key={value}
              className={`card-tab${lineOfWork === value ? " active" : ""}`}
              onClick={() => setLineOfWork(lineOfWork === value ? "" : value)}
            >
              {value} ({count})
            </button>
          ))}
        </div>
      )}

      {partnersQuery.data && groups.length === 0 ? (
        <div className="list">
          <div className="empty">
            {partners.length === 0 ? (
              <>
                <b>No cards yet</b>
                Scan a business card and it lands here, filed under its line of
                work.
              </>
            ) : (
              <>
                <b>No card matches</b>
                Nothing in the file matches that search.
              </>
            )}
          </div>
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.value} className="card-file-group">
            <h3>
              {group.value} <span className="sub">{group.cards.length}</span>
            </h3>
            <div className="card-file">
              {group.cards.map((partner) => (
                <CardTile
                  key={partner.id}
                  partner={partner}
                  image={
                    partner.card_front_path
                      ? images[partner.card_front_path]
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}

function CardTile({
  partner,
  image,
}: {
  partner: Partner;
  image: string | undefined;
}) {
  const phone =
    formatE164(partner.phone_mobile) ?? formatE164(partner.phone_office);
  return (
    <button
      className="card-file-tile"
      onClick={() => navigate(`partner=${partner.id}`)}
    >
      {image ? (
        <img src={image} alt={`${partnerDisplayName(partner)}'s card`} />
      ) : (
        <div className="card-file-blank">
          {partner.card_front_path ? "" : "Typed in, no photo"}
        </div>
      )}
      <span className="cust">{partnerDisplayName(partner)}</span>
      <span className="sub">
        {[partner.title, partner.company].filter(Boolean).join(" · ") ||
          "No company on the card"}
      </span>
      <span className="pill">{partnerLineOfWork(partner)}</span>
      {phone && <span className="sub">{phone}</span>}
    </button>
  );
}
